import { useEffect, useState } from 'react';
import { HelpCircle, Moon, Search, Sun } from 'lucide-react';
import { useI18n, useT } from '@/i18n/I18nProvider';
import { useUiStore } from '@/shared/theme/ui.store';
import { SearchPalette } from '@/features/search/SearchPalette';
import { Button, Dialog, DialogContent, Tooltip } from '@/shared/ui';

const isMac = typeof navigator !== 'undefined' && /mac/i.test(navigator.platform);
const MOD = isMac ? '⌘' : 'Ctrl';

/** Global search: Ctrl/⌘+K opens the command palette (customers, contacts, leads, tasks, approvals, pages). */
export function GlobalSearch() {
  const t = useT();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="hidden h-11 w-full max-w-md items-center gap-2.5 rounded-xl border border-sidebar-border bg-sidebar-accent/60 px-3.5 text-[13px] text-sidebar-foreground transition-colors hover:border-primary/40 hover:bg-sidebar-accent focus-visible:ring-2 focus-visible:ring-primary/40 md:flex"
      >
        <Search className="size-4 shrink-0" />
        <span className="flex-1 truncate text-left">{t('topbar.searchPlaceholder')}</span>
        <kbd className="rounded-md border border-sidebar-border bg-sidebar px-1.5 py-0.5 font-sans text-2xs font-semibold text-sidebar-foreground">
          {MOD}K
        </kbd>
      </button>
      <Button variant="ghost" size="icon" className="md:hidden" aria-label={t('topbar.search')} onClick={() => setOpen(true)}>
        <Search />
      </Button>

      <SearchPalette open={open} onOpenChange={setOpen} />
    </>
  );
}

/** Przełącznik jasny/ciemny jednym kliknięciem (pełny wybór z „systemowym” jest w menu użytkownika). */
export function ThemeToggle() {
  const t = useT();
  const theme = useUiStore((s) => s.theme);
  const setTheme = useUiStore((s) => s.setTheme);
  const isDark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  const label = isDark ? t('topbar.themeLight') : t('topbar.themeDark');
  return (
    <Tooltip content={label} side="bottom">
      <Button variant="ghost" size="icon" aria-label={label} onClick={() => setTheme(isDark ? 'light' : 'dark')}>
        {isDark ? <Sun /> : <Moon />}
      </Button>
    </Tooltip>
  );
}

/** Pomoc: skróty klawiaturowe i podpowiedzi. */
export function HelpButton() {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const rows: Array<[string, string]> = [
    [`${MOD} K`, t('topbar.helpSearch')],
    [t('ai.assistant.shortcut'), t('topbar.helpAssistant')],
  ];
  return (
    <>
      <Tooltip content={t('topbar.help')} side="bottom">
        <Button variant="ghost" size="icon" aria-label={t('topbar.help')} onClick={() => setOpen(true)}>
          <HelpCircle />
        </Button>
      </Tooltip>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent title={t('topbar.help')} closeLabel={t('common.close')} className="max-w-md">
          <p className="mb-3 text-sm text-muted-foreground">{t('topbar.helpIntro')}</p>
          <ul className="divide-y divide-border rounded-xl border border-border">
            {rows.map(([keys, what]) => (
              <li key={what} className="flex items-center justify-between gap-3 px-3 py-2.5 text-sm">
                <span>{what}</span>
                <kbd className="rounded-md border border-border bg-muted px-2 py-0.5 font-sans text-xs font-semibold">{keys}</kbd>
              </li>
            ))}
          </ul>
        </DialogContent>
      </Dialog>
    </>
  );
}

export { NotificationsBell } from '@/features/notifications/NotificationsBell';
